import React from 'react'
import Helmet from 'react-helmet'
import { useParams } from 'react-router'

import { useForTablet, Navbar, SplitLayout } from '@sketch/components'
import {
  DynamicLoadingPage,
  Footer,
  GenericErrorView,
} from '@sketch/modules-common'
import { useLayoutOverrideProps, useLayoutPortal, getItem } from '@sketch/utils'

import { Sidebar, NoWorkspaceSidebar } from '../Sidebar'

import {
  SelectorSection,
  HamburgerButton,
  HamburgerIcon,
  SidebarWrapper,
  Margin,
  NavbarPortalContainer,
} from './WorkspaceSidebarLayout.styles'

import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { useGetWorkspaces } from 'modules/workspace/operations'
import { localStorageKeys } from '@sketch/constants'

export interface LayoutPassDownProps {
  title: string
  hideFooter?: boolean
  hasNavbar?: boolean
}

export interface WorkspaceSidebarLayoutExtraProps {
  NavbarPortal: React.FunctionComponent<React.PropsWithChildren<{}>>
  SubheaderPortal: React.FunctionComponent<React.PropsWithChildren<{}>>
  useOverrideLayoutProps: (props: LayoutPassDownProps) => void
  workspace: WorkspaceMinimalFragment
}

interface RenderPropChildren {
  render: (props: WorkspaceSidebarLayoutExtraProps) => React.ReactElement
}

interface ComponentChildren {
  component: React.ComponentType<WorkspaceSidebarLayoutExtraProps>
}

export type WorkspaceSidebarLayoutProps = OneOf<
  RenderPropChildren,
  ComponentChildren
> & {
  title?: string
  hideFooter?: boolean
}

/**
 * In some views there is no need to have current workspace.
 * E.g. user settings, updates, discovery page and so on.
 *
 * So if there is no selected workspace, just pick first one from the list.
 * (if that list contains any items).
 */
const getCurrentWorkspace = (
  workspaceId?: string,
  workspaces?: WorkspaceMinimalFragment[]
): WorkspaceMinimalFragment | undefined => {
  if (!workspaces) return undefined

  const lastWorkspaceId =
    workspaceId || getItem(localStorageKeys.lastWorkspaceIdKey)
  if (lastWorkspaceId)
    return workspaces.find(({ identifier }) => identifier === lastWorkspaceId)

  return workspaces[0]
}

export const WorkspaceSidebarLayout: React.FC<WorkspaceSidebarLayoutProps> = props => {
  const params = useParams<{ workspaceId?: string }>()

  const {
    getOverriddenProps,
    useOverrideLayoutProps,
  } = useLayoutOverrideProps<LayoutPassDownProps>()

  const {
    title = 'Workspace',
    hideFooter,
    hasNavbar = true,
    render,
    component: Component,
  } = getOverriddenProps(
    props as WorkspaceSidebarLayoutProps & LayoutPassDownProps
  )

  // Reponsiveness
  const isTabletAndBigger = useForTablet()
  const isSmallerThanTablet = !isTabletAndBigger

  const { workspaces, loading, error } = useGetWorkspaces()

  // Layout hooks
  const [NavbarPortal, setNavbarPortalContainerRef] = useLayoutPortal()
  const [SubheaderPortal, setSubheaderPortalContainerRef] = useLayoutPortal()

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error) {
    return <GenericErrorView error={error} />
  }

  const workspace = getCurrentWorkspace(params.workspaceId, workspaces)

  const sidebar = (
    <SidebarWrapper>
      {!workspace || workspaces?.length === 0 ? (
        <NoWorkspaceSidebar />
      ) : (
        <Sidebar workspace={workspace} />
      )}
    </SidebarWrapper>
  )

  const childrenProps = {
    NavbarPortal,
    SubheaderPortal,
    useOverrideLayoutProps,

    // "workspace!" is used to avoid ts(2345) error with render() and <Component />.
    // This will not break anything because the check is being made before
    // calling does components.
    workspace: workspace!,
  }

  return (
    <>
      <Helmet>
        <meta name="theme-color" content="#F0F0F0" />
      </Helmet>
      <SplitLayout
        title={title}
        header={({ setSidebarLeftOpen, isSidebarLeftOpen }) => (
          <Navbar
            isSidebarLeftOpen={isSidebarLeftOpen}
            singleContent={hasNavbar && !isSmallerThanTablet}
          >
            {isSmallerThanTablet && (
              <SelectorSection>
                <HamburgerButton
                  aria-label="Hamburger menu button"
                  onClick={() => setSidebarLeftOpen?.(true)}
                >
                  <HamburgerIcon />
                </HamburgerButton>
              </SelectorSection>
            )}

            <NavbarPortalContainer
              ref={ref => setNavbarPortalContainerRef(ref)}
            />
          </Navbar>
        )}
        footer={!hideFooter && <Footer />}
        sidebar={sidebar}
      >
        <div ref={ref => setSubheaderPortalContainerRef(ref)} />

        <Margin>
          {render && render(childrenProps)}
          {Component && <Component {...childrenProps} />}
        </Margin>
      </SplitLayout>
    </>
  )
}
